/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Checkbox,
  Radio,
  CircularProgress,
  Box,
  Typography,
  Popover,
  Divider,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ManageSearch, Search, Check, HelpOutline, InfoOutlined, Clear } from '@mui/icons-material';

import api from '../../services/api';

export type SearchEntity =
  | 'products'
  | 'services'
  | 'suppliers'
  | 'clients'
  | 'erpUsers'
  | 'intraUsers'
  | 'costCenter'
  | 'derivation'
  | 'deposits'
  | 'transactions'
  | 'origins'
  | 'productionOrders'
  | 'stages'
  | 'lots';

export interface SearchResultItem {
  id: string | number;
  name: string;
  depositoOrigem?: string;
  saldoDisponivel?: number;
}

export interface GenericSearchInputProps {
  label: string;
  source: SearchEntity;
  value: string;
  onChange: (val: string, selectedItems?: SearchResultItem[]) => void;
  multiple?: boolean;
  companyId?: number;
  branchId?: number;
  productId?: string;
  derivationId?: string;
  depositId?: string;
  origin?: string;
  onlyActive?: boolean;
  required?: boolean;
  module?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

function parseRangeValue(rawInput: string): string[] {
  if (!rawInput || !rawInput.trim()) return [];

  const items = rawInput
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  const resultIds: string[] = [];

  items.forEach(item => {
    if (/^\d+\s*-\s*\d+$/.test(item)) {
      const [startStr, endStr] = item.split('-').map(s => s.trim());
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);

      if (!Number.isNaN(start) && !Number.isNaN(end) && start <= end && end - start <= 1000) {
        for (let i = start; i <= end; i++) {
          const idStr = String(i);
          if (!resultIds.includes(idStr)) {
            resultIds.push(idStr);
          }
        }
        return;
      }
    }

    if (!resultIds.includes(item)) {
      resultIds.push(item);
    }
  });

  return resultIds;
}

function HighlightText({ text, highlight }: { text: string; highlight: string }) {
  const theme = useTheme();

  if (!highlight || !highlight.trim()) {
    return <>{text}</>;
  }

  const tokens = highlight.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return <>{text}</>;

  const escapedTokens = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escapedTokens.join('|')})`, 'gi');
  const parts = String(text).split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = tokens.some(t => t.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <Box
            key={index}
            component="span"
            sx={{
              bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
              color: 'text.primary',
              fontWeight: 'bold',
              px: 0.3,
              borderRadius: '2px',
            }}
          >
            {part}
          </Box>
        ) : (
          part
        );
      })}
    </>
  );
}

export default function GenericSearchInput({
  label,
  source,
  value,
  onChange,
  required = false,
  multiple = false,
  companyId = 1,
  branchId = 1,
  productId = '',
  derivationId = '',
  depositId = '',
  origin = '',
  onlyActive = true,
  module = '',
  placeholder = 'Ex: 1, 2, 5-10',
  disabled = false,
  error = false,
  helperText,
  inputRef,
  onKeyDown,
}: GenericSearchInputProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalSearchTerm, setModalSearchTerm] = useState<string>('');
  const [executedSearchTerm, setExecutedSearchTerm] = useState<string>('');
  const [operator, setOperator] = useState<string>('contem');

  const [sortField, setSortField] = useState<'id' | 'name'>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const [inputHelpAnchor, setInputHelpAnchor] = useState<HTMLElement | null>(null);
  const [modalHelpAnchor, setModalHelpAnchor] = useState<HTMLElement | null>(null);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [tempSelected, setTempSelected] = useState<SearchResultItem[]>([]);

  const handleExecuteSearch = async (
    targetPage?: number,
    targetLimit?: number,
    targetSortField?: 'id' | 'name',
    targetSortOrder?: 'asc' | 'desc',
    overrideTerm?: string,
  ) => {
    const activePage = targetPage ?? page;
    const activeLimit = targetLimit ?? rowsPerPage;
    const activeSortField = targetSortField ?? sortField;
    const activeSortOrder = targetSortOrder ?? sortOrder;
    const termToSearch = overrideTerm !== undefined ? overrideTerm : modalSearchTerm;

    try {
      setIsLoading(true);
      setExecutedSearchTerm(termToSearch);

      const { data } = await api.get('search/generic', {
        params: {
          entity: source,
          companyId,
          branchId,
          productId,
          derivationId,
          depositId,
          origin,
          onlyActive,
          module,
          searchTerm: termToSearch,
          operator,
          page: activePage,
          limit: activeLimit,
          sortField: activeSortField,
          sortOrder: activeSortOrder,
        },
      });

      setResults(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 0);
    } catch (err: any) {
      console.error('Erro na pesquisa genérica:', err);
      const msg = err?.response?.data?.message || 'Erro ao realizar busca de dados.';
      setErrorMessage(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    if (disabled) return;
    setOpenModal(true);

    const initialTerm = value ? String(value).trim() : '';
    setModalSearchTerm(initialTerm);
    setExecutedSearchTerm(initialTerm);
    setOperator('contem');

    const parsedIds = parseRangeValue(value);
    const initialSelected: SearchResultItem[] = parsedIds.map(id => ({
      id,
      name: '',
    }));
    setTempSelected(initialSelected);

    handleExecuteSearch(0, rowsPerPage, sortField, sortOrder, initialTerm);
  };

  const handleSortChange = (field: 'id' | 'name') => {
    const isAsc = sortField === field && sortOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    handleExecuteSearch(0, rowsPerPage, field, newOrder);
  };

  const handleSelectItem = (item: SearchResultItem) => {
    if (multiple) {
      const exists = tempSelected.some(s => String(s.id) === String(item.id));
      if (exists) {
        setTempSelected(tempSelected.filter(s => String(s.id) !== String(item.id)));
      } else {
        setTempSelected([...tempSelected, item]);
      }
    } else {
      setTempSelected([item]);
    }
  };

  const handleSelectAllPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newItems = results.filter(r => !tempSelected.some(s => String(s.id) === String(r.id)));
      setTempSelected([...tempSelected, ...newItems]);
    } else {
      const currentIds = results.map(r => String(r.id));
      setTempSelected(tempSelected.filter(s => !currentIds.includes(String(s.id))));
    }
  };

  const isAllPageSelected =
    results.length > 0 && results.every(r => tempSelected.some(s => String(s.id) === String(r.id)));

  const isSomePageSelected =
    results.some(r => tempSelected.some(s => String(s.id) === String(r.id))) && !isAllPageSelected;

  const handleConfirmSelection = () => {
    if (tempSelected.length === 0) {
      setOpenModal(false);
      return;
    }

    if (multiple) {
      const ids = tempSelected.map(i => i.id).join(', ');
      onChange(ids, tempSelected);
    } else {
      onChange(String(tempSelected[0].id), tempSelected);
    }

    setOpenModal(false);
  };

  return (
    <>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        inputRef={inputRef}
        onKeyDown={onKeyDown}
        onChange={e => onChange(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Box display="flex" alignItems="center" gap={0.5}>
                {value && !disabled && (
                  <Tooltip title="Limpar campo">
                    <IconButton size="small" onClick={() => onChange('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Como preencher o campo? (Sintaxe)">
                  <IconButton size="small" onClick={e => setInputHelpAnchor(e.currentTarget)} disabled={disabled}>
                    <HelpOutline fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={disabled ? 'Campo desabilitado' : 'Abrir modal de pesquisa avançada'}>
                  <span>
                    <IconButton onClick={handleOpenModal} color="primary" edge="end" disabled={disabled}>
                      <ManageSearch />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={Boolean(inputHelpAnchor)}
        anchorEl={inputHelpAnchor}
        onClose={() => setInputHelpAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { p: 2.5, maxWidth: 360, borderRadius: 2, boxShadow: theme.shadows[8] },
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          color="primary"
          display="flex"
          alignItems="center"
          gap={1}
          mb={1}
        >
          <InfoOutlined fontSize="small" /> Sintaxe de Preenchimento do Campo
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
          Você pode digitar os códigos diretamente neste campo utilizando as seguintes regras:
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" fontWeight="bold" color="text.primary" display="block">
              1. Código Único:
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Digite o código exato. Ex: <code>100.001</code>
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" fontWeight="bold" color="text.primary" display="block">
              2. Múltiplos Códigos (Vírgula):
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Separe por vírgulas. Ex: <code>10, 25, 42</code>
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" fontWeight="bold" color="text.primary" display="block">
              3. Intervalo de Códigos (Traço):
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Informe início e fim com hífen. Ex: <code>100-150</code>
            </Typography>
          </Box>
        </Stack>

        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={() => setInputHelpAnchor(null)}
          sx={{ mt: 2, fontWeight: 'bold' }}
        >
          Entendido
        </Button>
      </Popover>

      <Popover
        open={Boolean(modalHelpAnchor)}
        anchorEl={modalHelpAnchor}
        onClose={() => setModalHelpAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { p: 2.5, maxWidth: 400, borderRadius: 2, boxShadow: theme.shadows[10] },
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          color="primary"
          display="flex"
          alignItems="center"
          gap={1}
          mb={1}
        >
          <InfoOutlined fontSize="small" /> Guia de Uso da Pesquisa Avançada
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" fontWeight="bold" color="text.primary" display="block" gutterBottom>
              Busca Multi-Palavras:
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Separe palavras por espaço no modal. Ex: <code>LOTE 2026</code>
            </Typography>
          </Box>
        </Stack>

        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={() => setModalHelpAnchor(null)}
          sx={{ mt: 2.5, fontWeight: 'bold' }}
        >
          Entendido
        </Button>
      </Popover>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '90vw', md: '85vw' },
            maxWidth: '1280px',
            height: { xs: '92vh', sm: '85vh' },
            maxHeight: '900px',
            borderRadius: 2.5,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Pesquisa Avançada - {label}</span>
          <Tooltip title="Guia de Instruções do Modal">
            <IconButton size="small" onClick={e => setModalHelpAnchor(e.currentTarget)}>
              <HelpOutline />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            p: { xs: 1.5, sm: 2.5 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: isDark ? 'grey.900' : 'grey.100',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Termo de Pesquisa"
                  placeholder="Digite para pesquisar..."
                  value={modalSearchTerm}
                  onChange={e => setModalSearchTerm(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleExecuteSearch(0, rowsPerPage);
                  }}
                  InputProps={{
                    endAdornment: modalSearchTerm ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setModalSearchTerm('')}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="select-operator-label">Comportamento</InputLabel>
                  <Select
                    labelId="select-operator-label"
                    value={operator}
                    label="Comportamento"
                    onChange={e => setOperator(e.target.value)}
                  >
                    <MenuItem value="contem">Contém</MenuItem>
                    <MenuItem value="nao_contem">Não contém</MenuItem>
                    <MenuItem value="inicia_com">Inicia com</MenuItem>
                    <MenuItem value="nao_inicia_com">Não inicia com</MenuItem>
                    <MenuItem value="termina_com">Termina com</MenuItem>
                    <MenuItem value="nao_termina_com">Não termina com</MenuItem>
                    <MenuItem value="igual">Igual a (Exato)</MenuItem>
                    <MenuItem value="diferente">Diferente de</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => handleExecuteSearch(0, rowsPerPage)}
                  startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <Search />}
                  sx={{ height: '40px', fontWeight: 'bold' }}
                >
                  Pesquisar
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: 1.5,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        bgcolor: isDark ? 'grey.900' : 'grey.200',
                        color: 'text.primary',
                        fontWeight: 'bold',
                      },
                    }}
                  >
                    <TableCell padding="checkbox" width={50}>
                      {multiple && (
                        <Checkbox
                          indeterminate={isSomePageSelected}
                          checked={isAllPageSelected}
                          onChange={handleSelectAllPage}
                          size="small"
                        />
                      )}
                    </TableCell>

                    <TableCell width={160}>
                      <TableSortLabel
                        active={sortField === 'id'}
                        direction={sortField === 'id' ? sortOrder : 'asc'}
                        onClick={() => handleSortChange('id')}
                      >
                        Código / Lote
                      </TableSortLabel>
                    </TableCell>

                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'name'}
                        direction={sortField === 'name' ? sortOrder : 'asc'}
                        onClick={() => handleSortChange('name')}
                      >
                        Descrição / Detalhe / Saldo
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" mt={1}>
                          Buscando registros...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : results.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum resultado encontrado.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    results.map(row => {
                      const isSelected = tempSelected.some(s => String(s.id) === String(row.id));

                      return (
                        <TableRow
                          key={row.id}
                          hover
                          selected={isSelected}
                          onClick={() => handleSelectItem(row)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            {multiple ? (
                              <Checkbox checked={isSelected} size="small" />
                            ) : (
                              <Radio checked={isSelected} size="small" />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            <HighlightText text={String(row.id)} highlight={executedSearchTerm} />
                          </TableCell>
                          <TableCell>
                            <HighlightText text={row.name} highlight={executedSearchTerm} />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 25, 50, 100]}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => handleExecuteSearch(newPage, rowsPerPage)}
                onRowsPerPageChange={e => {
                  const newLimit = parseInt(e.target.value, 10);
                  setRowsPerPage(newLimit);
                  handleExecuteSearch(0, newLimit);
                }}
                labelRowsPerPage="Itens por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              />
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            px: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight="bold">
            {tempSelected.length} item(ns) selecionado(s)
          </Typography>
          <Box display="flex" gap={1.5}>
            <Button onClick={() => setOpenModal(false)} color="inherit">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSelection}
              color="primary"
              variant="contained"
              startIcon={<Check />}
              sx={{ fontWeight: 'bold', px: 3 }}
            >
              Selecionar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(errorMessage)} autoHideDuration={4000} onClose={() => setErrorMessage('')}>
        <Alert severity="error" variant="filled" sx={{ fontWeight: 'bold' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
